<template>
  <gmap-map
    v-if="haveLocationFromAndTo"
    ref="map"
    :zoom="zoom"
    :center="center"
    :options="{ streetViewControl: false, fullscreenControl: false, mapTypeControl: false, zoomControl: false, panControl: false, scrollwheel: false, animatedZoom: false }"
    style="width: 100%; height: 400px">
    <template v-if="mapReady">
      <gmap-marker
        ref="locationFrom"
        v-if="haveLocationFrom"
        :position="{ lat: locationFrom.latitude, lng: locationFrom.longitude }"
        :draggable="false"
        :animation="0">
        <gmap-info-window  v-if="haveLocationFrom">
          <strong>From:</strong> {{ locationFrom.nameWithShortState }}<br>
        </gmap-info-window>
      </gmap-marker>
      <gmap-marker
        ref="locationTo"
        v-if="haveLocationTo"
        :position="{ lat: locationTo.latitude, lng: locationTo.longitude }"
        :draggable="false"
        :animation="0">
        <gmap-info-window v-if="haveLocationTo">
          <strong>To:</strong> {{ locationTo.nameWithShortState }}<br>
        </gmap-info-window>
      </gmap-marker>
      <gmap-polyline
        v-if="haveLocationFromAndTo"
        :draggable="false"
        :editable="false"
        :path="polylinePath"
        :options="{ strokeWeight: 2, strokeOpacity: 0.5, deepWatch: true }">
      </gmap-polyline>
    </template>
  </gmap-map>
</template>

<script>
  import { mapState } from 'vuex'
  import { isObject } from 'lodash'

  export default {
    props: {
      locationTo: {
        required: false,
        type: Object
      },
      locationFrom: {
        required: false,
        type: Object
      }
    },

    data () {
      return {
        mapReady: false,
        polylinePath: [],
        distanceBetweenLocations: null,
        bounds: {
          east: null,
          west: null,
          north: null,
          south: null
        },
        center: { lat: -27.0374585, lng: 133.2650289 },
        zoom: 5
      }
    },

    computed: {
      ...mapState('app', ['loading']),

      haveLocationFrom () {
        return isObject(this.locationFrom) && this.locationFrom.latitude !== null && this.locationFrom.longitude !== null
      },

      haveLocationTo () {
        return isObject(this.locationTo) && this.locationTo.latitude !== null && this.locationTo.longitude !== null
      },

      haveLocationFromAndTo () {
        return this.haveLocationFrom && this.haveLocationTo
      },

      longitudeDifference () {
        if (this.haveLocationFromAndTo) {
          if (this.locationFrom.longitude > this.locationTo.longitude) {
            return this.locationFrom.longitude - this.locationTo.longitude
          }

          return this.locationTo.longitude - this.locationFrom.longitude
        }

        return 0
      },

      latitudeDifference () {
        if (this.haveLocationFromAndTo) {
          if (this.locationFrom.latitude > this.locationTo.latitude) {
            return this.locationFrom.latitude - this.locationTo.latitude
          }

          return this.locationTo.latitude - this.locationFrom.latitude
        }

        return 0
      }
    },

    watch: {
      /**
       * Watch the application loading, once loaded, we'll resize the map.
       *
       * @return void
       */
      loading (loading) {
        if (loading) return

        this.$nextTick(() => (this.$emit('ready')))
      }
    },

    mounted () {
      this.$on('ready', this.draw)
      this.$on('refresh', this.draw)
    },

    beforeDestroy () {
      this.$off('ready', this.draw)
      this.$off('refresh', this.draw)
    },

    methods: {
      draw () {
        let google = window.google.maps

        this.calculateMapBounds()

        if (this.haveLocationFromAndTo) {
          const latLng = {
            from: new google.LatLng(this.locationFrom.latitude, this.locationFrom.longitude),
            to: new google.LatLng(this.locationTo.latitude, this.locationTo.longitude)
          }

          this.polylinePath = [latLng.from, latLng.to]

          this.distanceBetweenLocations = parseFloat(((google.geometry.spherical.computeDistanceBetween(latLng.from, latLng.to)) / 1000).toFixed(2))

          if (this.longitudeDifference > 15 || (this.latitudeDifference > 5 && this.latitudeDifference < 10)) {
            this.bounds.north += 5
            this.bounds.south += 1
          } else if (this.latitudeDifference < 5) {
            this.bounds.north += 1.25
            this.bounds.south += 1
          }

          if (this.longitudeDifference < 15) {
            this.bounds.west -= 2
            this.bounds.east += 2
          }

          this.$refs.map.fitBounds(this.bounds)
        } else {
          this.zoom = 5
          this.center = { lat: -27.0374585, lng: 133.2650289 }
        }

        this.mapReady = true

        if (this.$refs.map) {
          this.$nextTick(this.$refs.map.resizePreserveCenter)
        }
      },

      calculateMapBounds () {
        if (!this.haveLocationFromAndTo) {
          return
        }

        if (this.locationFrom.longitude > this.locationTo.longitude) {
          this.bounds.east = this.locationFrom.longitude
          this.bounds.west = this.locationTo.longitude
        } else {
          this.bounds.east = this.locationTo.longitude
          this.bounds.west = this.locationFrom.longitude
        }

        if (this.locationFrom.latitude > this.locationTo.latitude) {
          this.bounds.north = this.locationFrom.latitude
          this.bounds.south = this.locationTo.latitude
        } else {
          this.bounds.north = this.locationTo.latitude
          this.bounds.south = this.locationFrom.latitude
        }
      }
    }
  }
</script>



// WEBPACK FOOTER //
// DistanceMap.vue?3e87aaa2